import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StudentDirectory from "./StudentDirectory";
import StudentDashboard from "./dashboard/StudentDashboard";
import { normalizeTabKey as normalizeStudentTab } from "./dashboard/tabs/registry";
import {
  applyStudentStatusAutoTransitions,
  bulkUpdateStudentGrades,
  createStudent,
  fetchStudents,
  saveStudent,
} from "./studentFirestore";

export default function StudentManagement() {
  const location = useLocation();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newGrade, setNewGrade] = useState("");

  const [directoryState, setDirectoryState] = useState({
    searchTerm: "",
    filterStatus: "All",
    sortKey: "firstAttendNewest",
  });

  const routeState = useMemo(() => {
    const parts = location.pathname.replace(/\/+$/, "").split("/").filter(Boolean);
    const directoryIndex = parts.indexOf("directory");
    const studentId = directoryIndex >= 0 ? decodeURIComponent(parts[directoryIndex + 1] || "") : "";
    const tab = directoryIndex >= 0 ? decodeURIComponent(parts[directoryIndex + 2] || "") : "";
    return {
      studentId,
      tab,
      isDetailRoute: !!studentId,
    };
  }, [location.pathname]);

  // The detail page is a single route — the active tab lives in component state.
  // An initial tab may be requested via navigation state (e.g. "Conference
  // Management" from the directory) without putting it in the URL. With no
  // explicit request, normalizeStudentTab falls back to the default (Sessions).
  // Active tab is reflected in the URL (/students/directory/:id/:tab) so each
  // section is bookmarkable/shareable. Falls back to navigation state, then the
  // default tab.
  const initialTab = normalizeStudentTab(routeState.tab || location.state?.initialTab || "");

  useEffect(() => {
    loadStudents();
  }, []);

  const activeStudent = useMemo(() => {
    if (!routeState.isDetailRoute) return null;
    return (
      students.find((student) => student.id === routeState.studentId) ||
      (selectedStudent?.id === routeState.studentId ? selectedStudent : null)
    );
  }, [routeState.isDetailRoute, routeState.studentId, selectedStudent, students]);

  // Mark a student as "reviewed" the first time an admin opens their detail view.
  // This removes them from the Home "New Students to Follow Up" list and the Home
  // sidebar badge, while leaving them in the Student Directory untouched. We only
  // write once per student per session (markedReviewedRef) and skip students that
  // are already reviewed.
  const markedReviewedRef = useRef(new Set());
  useEffect(() => {
    const id = routeState.studentId;
    if (!routeState.isDetailRoute || !id) return;
    if (markedReviewedRef.current.has(id)) return;
    const student = activeStudent;
    if (!student) return; // wait until the student record has loaded
    if (student.followUpReviewedAt) {
      markedReviewedRef.current.add(id);
      return;
    }
    markedReviewedRef.current.add(id);
    const reviewedAt = new Date().toISOString();
    saveStudent(id, { followUpReviewedAt: reviewedAt })
      .then(() => {
        setStudents((prev) =>
          prev.map((item) => (item.id === id ? { ...item, followUpReviewedAt: reviewedAt } : item))
        );
        setSelectedStudent((prev) =>
          prev?.id === id ? { ...prev, followUpReviewedAt: reviewedAt } : prev
        );
        // Let the sidebar badge update right away.
        window.dispatchEvent(new Event("admin-attention-refresh"));
      })
      .catch(() => {
        // Allow a retry on a later open if the write failed.
        markedReviewedRef.current.delete(id);
      });
  }, [routeState.isDetailRoute, routeState.studentId, activeStudent]);

  async function loadStudents() {
    setLoadingStudents(true);
    try {
      let studentsData = await fetchStudents();

      // Lightweight auto-transition pass (no session-log scan):
      // Signed Up -> Enrolled, Enrolled -> Paused (after inactivity threshold).
      const autoTransitionResult = await applyStudentStatusAutoTransitions(studentsData);
      if ((Number(autoTransitionResult?.updatedStudents) || 0) > 0) {
        studentsData = await fetchStudents();
      }

      setStudents(studentsData);
    } catch (e) {
      console.error(e);
      alert("Failed to load students.");
    } finally {
      setLoadingStudents(false);
    }
  }

  function openCreateModal() {
    setNewName("");
    setNewGrade("");
    setIsCreateModalOpen(true);
  }

  async function submitNewStudent() {
    if (!newName.trim()) {
      alert("Please enter a student name.");
      return;
    }

    const gradeNumber = Number(newGrade);
    if (!newGrade.trim() || !Number.isInteger(gradeNumber)) {
      alert("Grade must be a valid integer.");
      return;
    }

    // Automatically set start date to today in YYYY-MM-DD format
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const startDate = `${yyyy}-${mm}-${dd}`;

      try {
        const created = await createStudent({ 
          name: newName, 
          grade: gradeNumber, 
          startDate 
        });
      setStudents((prev) => [...prev, created]);
      setIsCreateModalOpen(false); // Close modal on success
      const nextPath = `/students/directory/${encodeURIComponent(created.id)}`;
      if (location.pathname !== nextPath) {
        navigate(nextPath);
      }
    } catch (e) {
      alert(`Error creating student: ${e.message}`);
    }
  }

  function buildStudentPath(student) {
    return `/students/directory/${encodeURIComponent(student.id)}`;
  }

  function handleOpenStudent(student, options = {}) {
    const nextPath = buildStudentPath(student);
    if (options?.newTab) {
      window.open(nextPath, "_blank", "noopener");
      return;
    }
    // Tab choice travels in navigation state, not the URL, so the detail page
    // stays a single unified route. With no explicit tab, the default applies.
    navigate(nextPath, { state: { initialTab: normalizeStudentTab(options?.tab || "") } });
  }

  async function handleBulkUpdateGrades(updates) {
    const list = Array.isArray(updates) ? updates : [];
    if (!list.length) return;
    await bulkUpdateStudentGrades(list);
    const gradeById = new Map(list.map((u) => [String(u.id), Number(u.grade)]));
    setStudents((prev) =>
      prev.map((item) =>
        gradeById.has(String(item.id)) ? { ...item, grade: gradeById.get(String(item.id)) } : item
      )
    );
    setSelectedStudent((prev) =>
      prev && gradeById.has(String(prev.id)) ? { ...prev, grade: gradeById.get(String(prev.id)) } : prev
    );
  }

  async function handleUpdateStatus(studentId, nextStatus) {
    try {
      await saveStudent(studentId, { status: nextStatus });
      setStudents((prev) =>
        prev.map((item) => (item.id === studentId ? { ...item, status: nextStatus } : item))
      );
      setSelectedStudent((prev) =>
        prev?.id === studentId ? { ...prev, status: nextStatus } : prev
      );
    } catch (e) {
      alert(`Error updating status: ${e.message}`);
    }
  }

  async function handleUpdateSchedule(studentId, schedulePatch) {
    const patch = {
      scheduleType: String(schedulePatch?.scheduleType || ""),
      scheduleDate: String(schedulePatch?.scheduleDate || ""),
      scheduleTime: String(schedulePatch?.scheduleTime || ""),
    };
    try {
      await saveStudent(studentId, patch);
      setStudents((prev) =>
        prev.map((item) => (item.id === studentId ? { ...item, ...patch } : item))
      );
      setSelectedStudent((prev) =>
        prev?.id === studentId ? { ...prev, ...patch } : prev
      );
    } catch (e) {
      alert(`Error updating schedule: ${e.message}`);
    }
  }

  async function handleSaveStudent(studentId, changes, options = {}) {
    try {
      await saveStudent(studentId, changes);
      setStudents((prev) =>
        prev.map((item) => (item.id === studentId ? { ...item, ...changes } : item))
      );
      setSelectedStudent((prev) =>
        prev?.id === studentId ? { ...prev, ...changes } : prev
      );
      if (!options?.silent) alert("Saved.");
    } catch (e) {
      if (!options?.silent) alert(`Save failed: ${e.message}`);
      throw e;
    }
  }

  async function handleSkipConferenceCycle(studentId, totalHoursAtReset) {
    const patch = {
      conferenceCycleResetAt: new Date().toISOString(),
      conferenceCycleResetHours: Number(totalHoursAtReset || 0),
    };
    try {
      await saveStudent(studentId, patch);
      setStudents((prev) =>
        prev.map((item) => (item.id === studentId ? { ...item, ...patch } : item))
      );
      setSelectedStudent((prev) =>
        prev?.id === studentId ? { ...prev, ...patch } : prev
      );
    } catch (e) {
      alert(`Failed to skip conference indicator: ${e.message}`);
      throw e;
    }
  }

  if (routeState.isDetailRoute && activeStudent) {
    return (
      <StudentDashboard
        student={activeStudent}
        activeTabKey={initialTab}
        onSaveStudent={handleSaveStudent}
        onRequestTabChange={(tabKey) =>
          navigate(
            `/students/directory/${encodeURIComponent(routeState.studentId)}/${normalizeStudentTab(tabKey)}`
          )
        }
      />
    );
  }

  return (
    <>
      <StudentDirectory
        students={students}
        loading={loadingStudents}
        onRefresh={loadStudents}
        onCreateNew={openCreateModal}
        onOpenStudent={handleOpenStudent}
        onUpdateStatus={handleUpdateStatus}
        onUpdateSchedule={handleUpdateSchedule}
        onSkipConferenceCycle={handleSkipConferenceCycle}
        onBulkUpdateGrades={handleBulkUpdateGrades}
        getStudentHref={(student) => buildStudentPath(student)}
        searchTerm={directoryState.searchTerm}
        filterStatus={directoryState.filterStatus}
        sortKey={directoryState.sortKey}
        onDirectoryStateChange={(patch) => setDirectoryState((prev) => ({ ...prev, ...patch }))}
      />

      {isCreateModalOpen && (
        <div className="student-modal-overlay">
          <div className="student-modal">
            <div className="student-modal-header">Create New Student</div>
            <div className="student-modal-body">
              <div className="student-modal-input-group">
                <label>Full Name</label>
                <input
                  className="student-modal-input"
                  placeholder="e.g. John Doe"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
              <div className="student-modal-input-group">
                <label>Grade (Integer only)</label>
                <input
                  className="student-modal-input"
                  placeholder="e.g. 10"
                  type="number"
                  step="1"
                  value={newGrade}
                  onChange={(e) => setNewGrade(e.target.value)}
                />
              </div>
            </div>
            <div className="student-modal-footer">
              <button 
                className="student-directory__btn" 
                onClick={() => setIsCreateModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="student-directory__btn student-directory__btn--primary" 
                onClick={submitNewStudent}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
